import { type Dispatch, type MouseEvent, type SetStateAction } from 'react';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { type ClickType } from '@jsmdg/tracking';
import { ProductType } from '../../../shared/enums';
import { isClusteredBox, isExperience } from '../../../shared/product-safeguards';
import { type UseBuyboxReturn } from '../../hooks/useBuybox';
import { useProduct } from '../../hooks/useProduct';
import { useRedemptionCart } from '../../hooks/useRedemptionCart';
import { FeatureFlagVariant } from '../../types/enums/featureFlagVariant';
import { type ProductDetailContext } from '../../types/types';
import { BuyboxBody } from '../BuyboxBody/BuyboxBody';
import { BuyboxHeader } from '../BuyboxHeader/BuyboxHeader';
import { ProductGroup } from '../ProductGroup';
import { ProductStateContainer } from '../ProductStateContainer';
import { ClusterProductGroup } from './ClusterProductGroup';
import styles from './Buybox.module.scss';

export type BuyBoxProps = {
    readonly handleWishlistButtonClick: () => void;
    readonly setTargetElement: Dispatch<SetStateAction<HTMLElement | null>>;
    readonly setIsDiscountSelectOpen: Dispatch<SetStateAction<boolean>>;
    readonly isCheckoutDisabled: boolean;
    readonly isDiscountSelectOpen: boolean;
    readonly callToActionText: string;
    readonly buyboxProps: UseBuyboxReturn;
    readonly handleBuyboxButtonClick: (
        event?: MouseEvent<HTMLButtonElement>,
        clickType?: ClickType,
    ) => Promise<void> | void;
};

const Buybox = ({
    buyboxProps,
    callToActionText,
    handleBuyboxButtonClick,
    handleWishlistButtonClick,
    isCheckoutDisabled,
    isDiscountSelectOpen,
    setIsDiscountSelectOpen,
    setTargetElement,
}: BuyBoxProps): JSX.Element => {
    const product = useProduct();

    const { featureFlagsVariation } = useFragmentContext<ProductDetailContext>();
    const { isRedemptionCartFilled } = useRedemptionCart();
    const { productGroup, productId, type } = product;

    const buyboxIsSticky = featureFlagsVariation?.pdp_buybox_not_sticky !== FeatureFlagVariant.ON;

    return (
        <div
            data-testid="stickyBuyboxWrapper"
            className={classNames({
                'h-100': isExperience(product),
                'sticky-sm-top': type !== ProductType.EXPERIENCE && buyboxIsSticky,
            })}
        >
            <div
                className={classNames(
                    'd-flex flex-wrap align-items-center px-3x px-xs-6x pt-3x pt-xs-6x',
                    styles.buyBoxHeader,
                )}
            >
                <BuyboxHeader handleWishlistButtonClick={handleWishlistButtonClick} />
            </div>
            <ProductStateContainer />
            {isClusteredBox(product) ? (
                <ClusterProductGroup product={product} buyBoxProps={buyboxProps} />
            ) : (
                <ProductGroup
                    productType={type}
                    selectedProductId={productId}
                    items={productGroup}
                    isRedemptionCartFilled={isRedemptionCartFilled}
                />
            )}
            <BuyboxBody
                buyboxProps={buyboxProps}
                setTargetElement={setTargetElement}
                isDiscountSelectOpen={isDiscountSelectOpen}
                callToActionText={callToActionText}
                isCheckoutDisabled={isCheckoutDisabled}
                setIsDiscountSelectOpen={setIsDiscountSelectOpen}
                handleBuyboxButtonClick={handleBuyboxButtonClick}
            />
        </div>
    );
};

export { Buybox };
